import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collaborationAPI } from '../services/api';
import './AcceptInvite.css';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const isAuthenticated = !!localStorage.getItem('token');

  useEffect(() => {
    fetchInviteDetails();
  }, [token]);

  const fetchInviteDetails = async () => {
    try {
      const response = await collaborationAPI.getInviteByToken(token);
      setInvite(response.data.data.invite);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired invitation');
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Redirect to login with invite token in query params
      navigate(`/login?invite=${token}`);
      return;
    }

    setProcessing(true);
    setError('');

    try {
      await collaborationAPI.acceptInvite(token);
      navigate('/projects', { state: { message: 'Invitation accepted! You can now access the project.' } });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invitation');
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    setProcessing(true);

    try {
      await collaborationAPI.declineInvite(token);
      navigate('/projects', { state: { message: 'Invitation declined' } });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to decline invitation');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invite-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="accept-invite-container">
        <div className="invite-card error-card">
          <div className="invite-icon">❌</div>
          <h1>Invalid Invitation</h1>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-container">
      <div className="invite-card">
        <div className="invite-icon">📨</div>
        <h1>You've Been Invited!</h1>

        <div className="invite-details">
          <div className="invite-detail-row">
            <span className="invite-label">Project:</span>
            <span className="invite-value">{invite.projectName}</span>
          </div>
          {invite.projectDescription && (
            <div className="invite-detail-row">
              <span className="invite-label">Description:</span>
              <span className="invite-value">{invite.projectDescription}</span>
            </div>
          )}
          <div className="invite-detail-row">
            <span className="invite-label">Invited by:</span>
            <span className="invite-value">{invite.inviterName}</span>
          </div>
          <div className="invite-detail-row">
            <span className="invite-label">Expires:</span>
            <span className="invite-value">
              {new Date(invite.expiresAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {!isAuthenticated && (
          <div className="alert alert-info">
            <strong>Note:</strong> You need to sign in or create an account to accept this invitation.
          </div>
        )}

        <div className="invite-actions">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleAccept}
            disabled={processing}
          >
            {processing ? 'Processing...' : isAuthenticated ? 'Accept Invitation' : 'Sign In to Accept'}
          </button>
          <button
            className="btn btn-outline btn-lg"
            onClick={handleDecline}
            disabled={processing}
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
